import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { checkAuthStatus, enableLocalAuthBypass, clearAuthDiagnostic, getAuthDiagnostic, redirectToLogin } from '../utils/api';

const AuthContext = createContext();
const AUTO_LOGIN_ATTEMPT_KEY = 'SWA_AUTO_LOGIN_ATTEMPTED_AT';
const AUTO_LOGIN_ATTEMPT_TTL_MS = 2 * 60 * 1000;

const getAutoLoginAttemptAt = () => {
  try {
    const raw = localStorage.getItem(AUTO_LOGIN_ATTEMPT_KEY);
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch (_) {
    return 0;
  }
};

const hasRecentAutoLoginAttempt = () => {
  const attemptedAt = getAutoLoginAttemptAt();
  if (!attemptedAt) {
    return false;
  }

  const isRecent = (Date.now() - attemptedAt) < AUTO_LOGIN_ATTEMPT_TTL_MS;
  if (!isRecent) {
    try {
      localStorage.removeItem(AUTO_LOGIN_ATTEMPT_KEY);
    } catch (_) {}
  }

  return isRecent;
};

const markAutoLoginAttempt = () => {
  try {
    localStorage.setItem(AUTO_LOGIN_ATTEMPT_KEY, String(Date.now()));
  } catch (_) {}
};

const clearAutoLoginAttempt = () => {
  try {
    localStorage.removeItem(AUTO_LOGIN_ATTEMPT_KEY);
  } catch (_) {}
};

const buildSessionNotice = (diagnostic) => {
  const currentHost = diagnostic?.host || window.location.host;
  const diagnosticLabel = diagnostic?.type ? ` Latest check: ${diagnostic.type}.` : '';
  return `No Microsoft SSO session was found for this browser on ${currentHost}.${diagnosticLabel} Sign in again, keep using the same production URL, and make sure this browser profile is allowed to keep cookies for the app and Microsoft login domains.`;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authNotice, setAuthNotice] = useState(null);
  const [authDiagnostic, setAuthDiagnosticState] = useState(null);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  const isLocalDev = useCallback(() => (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('localhost')
  ), []);

  const login = useCallback((redirectUri) => {
    if (isLocalDev()) {
      console.log('Local Development: Enabling SSO bypass...');
      enableLocalAuthBypass();
      window.location.reload();
      return;
    }

    markAutoLoginAttempt();
    redirectToLogin(redirectUri || window.location.href);
  }, [isLocalDev]);

  const checkAuth = useCallback(async () => {
    if (isLoading && hasCheckedAuth) {
      return;
    }

    try {
      setIsLoading(true);
      setAuthError(null);
      setAuthNotice(null);

      console.log('Checking authentication status...');
      const authStatus = await checkAuthStatus();
      const latestDiagnostic = getAuthDiagnostic();
      setAuthDiagnosticState(latestDiagnostic);

      console.log('Auth status details:', authStatus);

      if (authStatus && authStatus.clientPrincipal) {
        console.log('User authenticated:', authStatus.clientPrincipal);
        setUser(authStatus.clientPrincipal);
        setIsAuthenticated(true);
        setAuthNotice(null);
        setAuthDiagnosticState(null);
        clearAuthDiagnostic();
        clearAutoLoginAttempt();
      } else {
        console.log('User not authenticated');
        setUser(null);
        setIsAuthenticated(false);

        const hasAttemptedAutoLogin = hasRecentAutoLoginAttempt();
        if (!isLocalDev() && !hasAttemptedAutoLogin) {
          console.log('No SWA session found. Attempting Entra login...');
          login(window.location.href);
          return;
        }

        if (!isLocalDev()) {
          setAuthNotice(buildSessionNotice(latestDiagnostic));
        }
      }
    } catch (error) {
      console.error('Authentication check failed:', error);

      if (error.message.includes('403') || error.message.includes('Forbidden')) {
        console.error('Azure Static Web App authentication not configured properly');
        setAuthError('Authentication service not configured. Please contact administrator.');
      } else {
        setAuthError(error.message);
      }

      setUser(null);
      setIsAuthenticated(false);
      setAuthDiagnosticState(getAuthDiagnostic());
      clearAutoLoginAttempt();
    } finally {
      setIsLoading(false);
      setHasCheckedAuth(true);
    }
  }, [hasCheckedAuth, isLoading, isLocalDev, login]);

  const logout = useCallback(() => {
    clearAutoLoginAttempt();
    window.location.href = '/.auth/logout';
  }, []);

  useEffect(() => {
    if (!hasCheckedAuth) {
      checkAuth();
    }
  }, [checkAuth, hasCheckedAuth]);

  const enableLocalBypass = useCallback(() => {
    enableLocalAuthBypass();
    checkAuth();
  }, [checkAuth]);

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    authError,
    authNotice,
    authDiagnostic,
    login,
    logout,
    refreshAuth: checkAuth,
    enableLocalBypass
  }), [user, isAuthenticated, isLoading, authError, authNotice, authDiagnostic, login, logout, checkAuth, enableLocalBypass]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
